"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { generateImageURL } from "@/lib/utils";
import type { ProjectProperty } from "./types";

type Folder = "site_plan" | "unit_mix_breakdown";

/** Site plan and unit mix images, two per row; each opens full screen with arrows. */
export function ProjectPlanGrid({ property, folder, files }: { property: ProjectProperty; folder: Folder; files: string[] }) {
  const label = `${property.title || property.name} ${folder === "site_plan" ? "site plan" : "unit mix"}`;
  const images = files.map((file) => generateImageURL(property.name, folder, file));
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    if (active == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((i) => (i == null ? 0 : (i + 1) % images.length));
      if (e.key === "ArrowLeft") setActive((i) => (i == null ? 0 : (i - 1 + images.length) % images.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, images.length]);

  if (images.length === 0) return null;

  return (
    <>
      <div className="grid gap-5 md:grid-cols-2">
        {images.map((src, i) => (
          <button
            key={src + i}
            type="button"
            onClick={() => setActive(i)}
            aria-label={`Open ${label} ${i + 1} of ${images.length} full screen`}
            className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-line bg-white p-3 text-left"
          >
            <Image src={src} alt={`${label} ${i + 1}`} fill unoptimized className="object-contain p-4 transition-transform duration-300 group-hover:scale-[1.02]" sizes="(min-width: 768px) 50vw, 100vw" />
            <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-ink/85 px-3 py-1.5 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
              <Maximize2 aria-hidden="true" className="size-3.5" />
              View full screen
            </span>
          </button>
        ))}
      </div>

      {active != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-4 md:p-8" role="dialog" aria-modal="true" aria-label={label} onMouseDown={(e) => { if (e.target === e.currentTarget) setActive(null); }}>
          <div className="relative h-[85vh] w-full max-w-7xl">
            <Image src={images[active]} alt={`${label} ${active + 1}`} fill unoptimized className="object-contain" sizes="100vw" />
            {images.length > 1 && (
              <>
                <button type="button" onClick={() => setActive((i) => ((i ?? 0) - 1 + images.length) % images.length)} aria-label="Previous image" className="absolute left-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-lg">
                  <ChevronLeft aria-hidden="true" className="size-5" />
                </button>
                <button type="button" onClick={() => setActive((i) => ((i ?? 0) + 1) % images.length)} aria-label="Next image" className="absolute right-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-lg">
                  <ChevronRight aria-hidden="true" className="size-5" />
                </button>
              </>
            )}
            <button type="button" onClick={() => setActive(null)} aria-label="Close" className="absolute right-0 top-0 rounded-full bg-white p-3 text-ink shadow-lg">
              <X aria-hidden="true" className="size-5" />
            </button>
            <p className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-ink">{active + 1} of {images.length}</p>
          </div>
        </div>
      )}
    </>
  );
}
